/**
 * The payment request, as the backend keeps one.
 *
 * It lives here rather than in any one feature's types because more than one
 * of them raises a payment — a client, from their Payments tab, and an enquiry,
 * from the button that turns it into a client — and the record is the same
 * either way, down to the field: only the path it hangs off differs, and its
 * `source` says which one that was. The same reasoning that put
 * `PaginatedResponse` here: the shape belongs to the API rather than to
 * whatever the money was asked of.
 *
 * snake_case exactly as the backend sends it. Only a service should touch
 * these — each maps them to its own feature's types, so the API's casing and
 * its enum constants stop at the service boundary.
 */

/**
 * The money received against a request, once there is any.
 * `null` on the response until then.
 */
export interface PaymentReceiptResponse {
  public_id: string;
  /** A decimal string, the way the backend keeps money: `"2000.00"`. */
  paid_amount: string;
  /** ISO 4217: `INR`. */
  currency: string;
  /** ISO date, `2026-08-12` — a day, not a moment. */
  received_on: string;
}

/** The stretch of plan a paid request opened. `null` until it is paid. */
export interface EngagementResponse {
  /** ISO dates, `2026-08-10`. */
  start_date: string;
  end_date: string;
}

/**
 * One payment request, whole — what the `POST` answers with and what a row of
 * the history `GET` is.
 *
 * The person's own details (`name`, `place`, `goal`) are repeated onto every
 * one because the invoice is a document in its own right: it says what was
 * true when it was raised, not what the record it was raised against says
 * today.
 */
export interface PaymentRequestResponse {
  public_id: string;
  /** The backend's own, human-facing number: `NIV/W1/002`. */
  invoice_number: string;
  /** What the request was raised against — `CLIENT`, or `INQUIRY`. */
  source: string;
  name: string;
  place: string;
  goal: string;
  /**
   * The plan as it was sold, by name rather than by id.
   *
   * `package_name` is what the backend sends: a plan is filed as a package
   * there. `service_name` is what the same field was called before that
   * rename, and `service` now names a different record altogether - see the
   * note over `API_ENDPOINTS.services`. Both are optional and both are read,
   * through `paymentPlanName`, so an invoice raised either side of the change
   * still draws its plan.
   */
  package_name?: string;
  /** What `package_name` was called before the rename. Still read. */
  service_name?: string;
  /** Its duration, as the label the catalog carries: `6 Months`. */
  duration_label: string;
  /** Decimal strings, all three: what it lists at, what came off, what is due. */
  package_price_amount?: string;
  /** What `package_price_amount` was called before the rename. Still read. */
  service_price_amount?: string;
  discount_amount: string;
  amount_due: string;
  currency: string;
  /** An enum constant: `AWAITING_PAYMENT`, `CONFIRMED`, `CANCELLED`. */
  status: string;
  /** ISO datetime the request was raised. */
  generated_on: string;
  /** ISO datetimes, each `null` until the thing it dates has happened. */
  sent_at: string | null;
  email_failed_at: string | null;
  cancelled_at: string | null;
  /** ISO *date* — the day the money came in, not the moment. */
  paid_on: string | null;
  /**
   * ISO date the stretch of plan this request bought runs out on — the same
   * day as `engagement.end_date`, lifted to the top level. `null` until the
   * request is paid, since nothing has started running.
   */
  ends_on: string | null;
  payment: PaymentReceiptResponse | null;
  engagement: EngagementResponse | null;
}

/**
 * The body both `POST /api/v1/clients/{id}/payment-requests/` and
 * `POST /api/v1/inquiries/{id}/payment-requests/` take.
 *
 * Only what the dietitian decides: which plan, and what to charge for it.
 * The invoice number, the discount that charge works out to, and the details
 * on the invoice are the backend's, and come back on the response.
 */
export interface CreatePaymentRequestPayload {
  /**
   * The plan's `public_id`, as its own list carries it.
   *
   * `package` rather than the `service` this used to be, matching the rename
   * on the way back — `package_name` and `package_price_amount` above. The
   * backend files a plan as a package, and `service` now names the
   * practitioner service, which is a different record entirely.
   */
  package: string;
  /** A decimal string, the way the backend keeps money: `"2000.00"`. */
  amount_due: string;
  /**
   * ISO date the engagement should run from. Optional — left off, the backend
   * dates it itself, which is what the app does: nothing on the request dialog
   * asks for a start date.
   */
  start_date?: string;
}

/**
 * The body `POST /api/v1/payment-requests/{public_id}/confirm/` takes.
 *
 * One field, and it is the one the backend cannot work out for itself: no
 * gateway reports a UPI transfer, a bank credit or an envelope of cash back to
 * it, so the day the money arrived is the dietitian's to say.
 */
export interface ConfirmPaymentRequestPayload {
  /** ISO date, `2026-08-13` — a day, not a moment. */
  received_on: string;
}

/** Rupees as the backend keeps them: two decimal places, as a string. */
export const toAmountDue = (amount: number): string => amount.toFixed(2);

/**
 * A request's amount as a number. `Number("")` is 0 and `Number(undefined)` is
 * NaN, so both a missing amount and an unparseable one land on 0 rather than
 * painting "₹ NaN" onto a screen.
 */
export const toAmountNumber = (amount: string | undefined): number =>
  Number(amount) || 0;

/**
 * Whether the money is in.
 *
 * The backend's own `status` decides it wherever the app knows the constant.
 * Anything it has not heard of falls back to the row itself, which says the
 * same thing in a form that cannot go stale: a request with a payment against
 * it has been paid. A state the app has never seen therefore reads as still
 * outstanding — a question open — rather than as settled.
 */
export const isPaymentRequestPaid = (
  response: PaymentRequestResponse
): boolean => {
  const state = (response.status ?? "").toUpperCase();
  if (state === "CONFIRMED" || state === "PAID") return true;
  return !!(response.payment || response.paid_on);
};

/** Whether the request was called off, read the same way. */
export const isPaymentRequestCancelled = (
  response: PaymentRequestResponse
): boolean =>
  (response.status ?? "").toUpperCase() === "CANCELLED" ||
  !!response.cancelled_at;

/**
 * The plan an invoice was raised for, under whichever name the backend sent
 * it: `package_name` now, `service_name` on a response from before the
 * rename. `""` where it named neither, which every caller draws as a dash.
 *
 * Here rather than in each service because all four of them read this field —
 * the client's plan card, their Payments tab, an enquiry's payments and the
 * Revenue table — and a rename read in three places out of four is a blank
 * cell on the fourth.
 */
export const paymentPlanName = (response: PaymentRequestResponse): string =>
  response.package_name || response.service_name || "";

/** What that plan lists at, read the same way. A decimal string, or `""`. */
export const paymentPlanPrice = (response: PaymentRequestResponse): string =>
  response.package_price_amount || response.service_price_amount || "";
